<template>
	<section>
		<section id="results">
			<section class="results-container" ref="results">
				<div class="top-options">
					<!-- item type toggle -->
					<div class="type-toggles">
						<button
							v-if="advancedSearch"
							@click="changeType('all')"
							class="type-toggle"
							:class="{ selected: type == 'all' }"
						>
							{{ $t("labels.all_results") }} ({{ resultsCount }})
						</button>
						<button
							@click="changeType('artifact')"
							class="type-toggle"
							:disabled="artifact_count == 0"
							:class="{ selected: type == 'artifact' }"
						>
							{{ $t("labels.artifacts") }} ({{ artifact_count }})
						</button>
						<button
							@click="changeType('archive')"
							class="type-toggle"
							:disabled="archive_count == 0"
							:class="{ selected: type == 'archive' }"
						>
							{{ $t("labels.archives") }} ({{ archive_count }})
						</button>
					</div>
					<div class="sort-view">
						<div>
							<!-- toggle refine results in mobile -->
							<button id="facet-toggle" ref="facetToggle" @click="openFacets">
								<span class="sr-only">{{ $t("labels.refine_results") }}</span>
								<span class="icon-filter" aria-hidden="true"></span>
							</button>

							<Dropdown
								:options="sortOptions"
								:selected="sort_by"
								selectName="sortBy"
								:disabled="!hasTypeResults"
								@update="setSort"
							></Dropdown>
						</div>

						<!-- results view type  -->
						<div>
							<button
								@click="changeView('grid')"
								class="view-toggle grid"
								:class="{ selected: gridView }"
								:disabled="!hasTypeResults"
							>
								<span class="sr-only">{{ $t("labels.grid_view") }}</span>
							</button>
							<button
								@click="changeView('list')"
								class="view-toggle list"
								:class="{ selected: !gridView }"
								:disabled="!hasTypeResults"
							>
								<span class="sr-only">{{ $t("labels.list_view") }}</span>
							</button>
							<button id="print" @click="printPage">
								<span class="label-small">
									<span class="sr-only">{{ $t("labels.print_detail") }}</span>
								</span>
							</button>
							<router-link :to="{ name: 'saved-list' }" class="nav-link saved hide-flex-desktop">
								<span class="sr-only">{{ $t("menu.saved") }}</span>
								<span class="saved-count" aria-hidden="true">
									<span>{{ saved_count }}</span>
								</span>
								<span
									class="icon-bookmark"
									:class="{ empty: saved_count == 0 }"
									aria-hidden="true"
								></span>
							</router-link>
						</div>
					</div>
					<MultiSelect class="hide-desktop" itemType="results"></MultiSelect>
				</div>
				<div class="facets-results" v-if="results_loaded">
					<!-- facets -->
					<Facets
						@removeFacet="removeFacet"
						@addFacet="addFacet"
						@clearFacets="refineResults"
						@closed="closeFacets"
						ref="facets"
					></Facets>
					<div
						v-if="!hasTypeResults && !search_loading && counts_loaded"
						class="no-results"
					>
						<h2>{{ $t("results_page.no_results_title") }}</h2>
						<p>{{ $t("results_page.no_results_text") }}</p>
					</div>
					<div
						v-else-if="hasTypeResults && resultsCount"
						class="grid-list-container"
					>
						<div class="print-search-query">
							<dl>
								<!-- basic search query -->
								<div v-if="search_type == 'basic'">
									<dt>
										{{ $t("menu.search") }}
									</dt>
									<dd>
										{{ query[2] }}
									</dd>
								</div>

								<!-- advanced search query -->
								<div v-if="search_type == 'advanced'">
									<div v-for="(value, i) of printableQuery" :key="`query-${i}`">
										<dt>
											{{ value.searching }}
										</dt>
										<dd>
											{{ value.term }}
											<span v-if="value.boolean">({{ value.boolean }})</span>
										</dd>
									</div>
									<div v-for="(date, i) of printableDate" :key="`date-${i}`">
										<div v-for="(value, key) in date" :key="key">
											<dt>
												{{ key }}
											</dt>
											<dd>
												{{ value }}
											</dd>
										</div>
									</div>
									<div></div>
								</div>

								<!-- facets -->
								<div
									class="print-facet"
									v-for="(facet, value, i) of printableFacets"
									:key="i"
								>
									<dt>
										{{ value }}
									</dt>
									<dd v-for="(val, i) in facet" :key="val">
										{{ val }}<span v-if="i != facet.length - 1">, </span>
									</dd>
								</div>
							</dl>
						</div>
						<div>
							<!-- result views -->
							<ResultsGrid v-if="gridView" :results="resultsList"></ResultsGrid>
							<ResultsList
								v-if="!gridView"
								:results="resultsList"
							></ResultsList>
						</div>

						<!-- pagination -->
						<Pagination 
							v-if="needsPagination"
							:currentPage="currentPage"
							:totalPages="totalPages"
							@turn="getPage"></Pagination>
					</div>
				</div>
			</section>
			<SelectedBar action="save"></SelectedBar>
		</section>
		<section class="more-links">
			<SearchingForMore></SearchingForMore>
			<ResourceCenter></ResourceCenter>
		</section>
		<JsonLD></JsonLD>
	</section>
</template>

<script>
import { mapGetters } from "vuex";
import JsonLD from "../components/JsonLD";
import ResultsGrid from "../components/ResultsGrid";
import ResultsList from "../components/ResultsList";
import Dropdown from "../components/Dropdown";
import Facets from "../components/Facets";
import ResourceCenter from "../components/ResourceCenter";
import SearchingForMore from "../components/SearchingForMore";
import SelectedBar from "../components/SelectedBar";
import MultiSelect from "../components/MultiSelect";
import Pagination from "../components/Pagination";

export default {
	components: {
		JsonLD,
		ResultsGrid,
		ResultsList,
		Dropdown,
		Facets,
		ResourceCenter,
		SearchingForMore,
		SelectedBar,
		MultiSelect,
		Pagination,
	},
	props: {
		advancedSearch: Boolean,
	},
	data: function() {
		return {
			sortOptionList: {
				artifact: [
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"number_asc"
						],
						value: "number,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"number_desc"
						],
						value: "number,desc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"obj_name_asc"
						],
						value: "title,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"obj_name_desc"
						],
						value: "title,desc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"name_asc"
						],
						value: "person,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"name_desc"
						],
						value: "person,desc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"date_asc"
						],
						value: "date,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"date_desc"
						],
						value: "date,desc",
					},
				],
				archive: [
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"number_asc"
						],
						value: "number,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"number_desc"
						],
						value: "number,desc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"title_asc"
						],
						value: "title,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"title_desc"
						],
						value: "title,desc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"name_asc"
						],
						value: "person,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"name_desc"
						],
						value: "person,desc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"date_asc"
						],
						value: "date,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"date_desc"
						],
						value: "date,desc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"call_num_asc"
						],
						value: "call_num,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"call_num_desc"
						],
						value: "call_num,desc",
					},
				],
				all: [
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"title_asc"
						],
						value: "title,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"title_desc"
						],
						value: "title,desc",
					},
				],
			},
			printableFacets: [],
		};
	},
	computed: {
		...mapGetters({
			query: "search/query",
			type: "results/type",
			counts_loaded: "results/counts_loaded",
			results: "results/list_results",
			results_loaded: "results/list_loaded",
			results_loading: "results/list_loading",
			search_loading: "search/loading",
			archive_count: "results/archive_count",
			artifact_count: "results/artifact_count",
			all_count: "results/all_count",
			view: "results/view",
			currentPage: "search/page",
			sort: "search/sort",
			order: "search/order",
			page: "search/page",
			search_type: "search/search_type",
			url_query: "search/url_query",
			sort_by: "results/sort_by",
			selected_facets: "search/selected_facets",
			all_facets: "search/facets",
			site: "search/site",
			printable_query: "results/printable_query",
			printable_date: "results/printable_date",
			saved_count: "list/saved_count",
		}),
		hasTypeResults() {
			let s = `${this.type}_count`;
			return this[s] > 0;
		},
		mainQuery() {
			if (!this.query) return;
			return this.query[0] !== "all" ? this.query[2] : this.query[1];
		},
		gridView() {
			return this.view == "grid";
		},
		sortOptions() {
			let options = this.sortOptionList[this.type];
			if (
				this.site == "cwm" &&
				!options.find((i) => i.value.includes("service"))
			) {
				let serviceSortOptions = [
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"service_asc"
						],
						value: "service,asc",
					},
					{
						text: this.$i18n.messages[this.$i18n.locale].sort_options[
							"service_desc"
						],
						value: "service,desc",
					},
				];
				options.splice(6, 0, ...serviceSortOptions);
			}
			return options;
		},
		resultsCount() {
			if (!this.results) return 0;
			if (this.search_type == "basic") {
				return this.all_count;
			} else {
				return this.artifact_count + this.archive_count;
			}
		},
		resultsList() {
			if (!this.results) return [];
			return this.results.results.length > 0 ? this.results.results : [];
		},
		pageContent() {
			let min = 1;
			let max = Math.min(24, this.resultsCount);
			return `${min}–${max}`;
		},
		totalPages() {
			let itemsPerPage = this.view == "grid" ? 24 : 100;
			let resultsMap = {
				all: this.resultsCount,
				artifact: this.artifact_count,
				archive: this.archive_count,
			};
			let results = resultsMap[this.type];
			return new Array(Math.ceil(results / itemsPerPage));
		},
		needsPagination() {
			return this.totalPages.length > 1;
		},
		printableQuery() {
			let searchingMap = this.$i18n.t(`search_fields_reverse_map`);
			let booleanMap = this.$i18n.t(`boolean_reverse_map`);

			let readable = [];
			if (this.printable_query.length > 0) {
				this.printable_query.forEach((q) => {
					let newQ = {};
					newQ.term = q.term;
					newQ.searching = searchingMap[q.searching];
					newQ.boolean = q.boolean ? booleanMap[q.boolean] : null;
					readable.push(newQ);
				});
			}

			return readable;
		},
		printableDate() {
			let wordMap = {
				earliest: this.$i18n.t(`labels.starting_date`),
				latest: this.$i18n.t("labels.ending_date"),
				date: this.$i18n.t("labels.date"),
			};

			let readableArchive = [];
			let readableArtifact = [];
			for (let d in this.printable_date) {
				let newD = {};
				newD[wordMap[d]] = this.printable_date[d];
				if (d == "earliest" || d == "latest") {
					readableArtifact.push(newD);
				} else {
					readableArchive.push(newD);
				}
			}
			if (this.type == "archive") {
				return readableArchive;
			} else {
				return readableArtifact;
			}
		},
	},
	watch: {
		search_type: function() {
			if (this.results) {
				// this.$store.dispatch('results/clear');
			}
		},
		selected_facets: function() {
			this.setPrintableFacets();
		},
	},
	methods: {
		openFacets() {
			this.$refs.facets.expand();
		},
		closeFacets() {
			this.$refs.facetToggle.focus();
		},
		changeView(view) {
			this.$store.dispatch("results/view", view);
			let size = this.gridView ? 24 : 100;
			let page = 1;

			if (this.gridView) {
				let items = this.page * 100 - 80;
				page = Math.max(1, Math.floor(items / 24));
			} else {
				let items = this.page * 24;
				page = Math.max(1, Math.floor(items / 100));
			}

			let data = {
				size: size,
				page: page,
				view: view,
			};
			this.refineResults(data, false);
		},
		changeType(type) {
			let data = {
				type: type,
				page: 1,
			};
			this.refineResults(data, false);
			setTimeout(() => {
				if (this.type == "archive") {
					this.changeView("list");
				} else {
					this.changeView("grid");
				}
			}, 100);
		},
		getPage(page) {
			if (page <= this.totalPages.length && page > 0) {
				this.refineResults({ page: page }, false);
			}
		},
		setSort(sort) {
			if (sort != this.sort_by) {
				let parts = sort.split(",");
				let data = {
					sort: parts[0],
					order: parts[1],
				};
				this.refineResults(data);
			}
		},
		refineResults(data, clearSelected = true) {
			let oldQ = this.$route.query;
			let type = data.type ? data.type : oldQ.type;

			if (clearSelected) this.$store.dispatch('list/clearSelected')

			if (type != "archive" && oldQ.sort == "call_num") {
				data.sort = "number";
			}

			if (type == "all" && oldQ.sort != "title") {
				data.sort = "title";
			}

			let newQ = {
				...oldQ,
				type: type,
				sort: data.sort || oldQ.sort || "title",
				page: data.page || oldQ.page || 1,
				view: data.view || oldQ.view || "grid",
				order: data.order || oldQ.order || "asc",
				size: data.size || oldQ.size || 24,
			};

			let urlQuery = this.queryWithFacets(newQ);

			let top = this.$refs.results;
			this.$emit("loaded", top.getBoundingClientRect()["y"]);
			let hash = window.location.hash
			this.$router
				.replace({ name: `${this.search_type}-search`, query: urlQuery })
				.then(() => {
					
					newQ.urlString = this.$route.fullPath.split("?")[1];
					this.$store.dispatch("search/update", newQ);
					if (hash.length > 0) {
						window.location.hash = hash
					}
				});
			this.setPrintableFacets();
		},
		search_url() {
			return this.search_type == "basic"
				? "/"
				: `/${this.$i18n.messages[this.$i18n.locale].routes.advanced_search}/`;
		},
		addFacet(facet) {
			this.$store.dispatch("search/addFacet", facet).then(() => {
				this.refineResults({ page: 1 });
			});
		},
		removeFacet(facet) {
			this.$store.dispatch("search/removeFacet", facet).then(() => {
				this.refineResults({ page: 1 });
			});
		},
		queryWithFacets(currentQuery) {
			let facetQuery = {};

			let imageField = "media.mime_type";
			if (currentQuery[imageField] && !this.selected_facets[imageField]) {
				delete currentQuery[imageField];
			}
			let mediaField = "media";
			if (currentQuery[mediaField] && !this.selected_facets[mediaField]) {
				delete currentQuery[mediaField];
			}

			for (let facetName in this.all_facets) {
				if (currentQuery[facetName] && !this.selected_facets[facetName]) {
					delete currentQuery[facetName];
				}
			}

			if (Object.keys(this.selected_facets).length) {
				for (let key in this.selected_facets) {
					facetQuery[key] = this.selected_facets[key].join("::");
				}
			}
			let newQuery = { ...currentQuery, ...facetQuery };

			return newQuery;
		},
		printPage() {
			window.print();
		},
		setPrintableFacets() {
			let searchingMap = this.$i18n.t(`facet_options_map`);
			let readable = {};

			for (const f in this.selected_facets) {
				let value = this.selected_facets[f];
				if (value[0] == "_exists") value = [this.$i18n.t("labels.true")];
				readable[searchingMap[f]] = value;
			}

			this.printableFacets = readable;
		},
	},
	mounted() {
		this.setPrintableFacets();

		// get the sticky element
		const stickyElm = document.querySelector('.top-options');

		const observer = new IntersectionObserver( 
			([e]) => e.target.classList.toggle('isSticky', e.intersectionRatio < 1),
			{threshold: [1]}
		);

		observer.observe(stickyElm);
	},
};
</script>

<style scoped lang="scss">
#results {
	background: $white;
}

.results-container,
.no-results {
	max-width: 1600px;
	padding: 0 $side-padding $s4;

	h2 {
		margin-top: 1rem;
	}
}
.grid-list-container {
	width: 100%;
}
.no-results {
	max-width: 900px;
	p {
		white-space: pre-wrap;
	}
}

.top-options {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 2rem;
	position: sticky;
	top: -1px;
	padding-top: 1.938rem;
	background: $white;
	align-self: flex-start;
	z-index: 5;

	.type-toggle {
		cursor: pointer;
		background: none;
		border: none;
		text-decoration: none;
		padding: 0;
		text-transform: capitalize;
		font-weight: 400;
		font-family: Arial, sans-serif;
		font-size: 1rem;
		color: $dark-gray1;
		&.selected {
			text-decoration: underline;
			text-underline-offset: 3px;
		}

		&:not(:last-of-type) {
			&:after {
				content: "";
				position: relative;
				margin: 0 $s2;
				height: 100%;
				width: 1px;
				border-right: solid 1px $dark-gray1;
			}
		}

		&:focus,
		&:active {
			border: none;
			text-decoration: underline;
		}
	}

	.sort-view {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-bottom: $s1;

		> div {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}
		.view-toggle {
			cursor: pointer;
			height: 28px;
			width: 28px;
			margin-left: 1.875rem;
			color: $med-gray1;
			position: relative;
			background: none;
			border: none;

			&.grid:after {
				position: absolute;
				content: url("../assets/images/icon-grid.svg");
				opacity: 0.4;
				width: 28px;
				top: 0;
				left: 0;
			}

			&.list {
				margin-right: 2rem;
				margin-left: $s2;
				&:after {
					position: absolute;
					content: url("../assets/images/icon-list.svg");
					opacity: 0.4;
					width: 28px;
					top: 0;
					left: 0;
				}
			}
			&.selected {
				color: $dark-gray1;
				&:after {
					opacity: 1;
				}
			}
		}

		button#print {
			cursor: pointer;
			background: none;
			border: none;
			display: inline-flex;
			align-items: center;
			padding-top: 9px;

			span {
				padding-right: 0.1rem;
				color: $menu-gray;
			}

			&:after {
				content: url("../assets/images/icon-print.svg");
				display: flex;
				justify-content: center;
				align-items: center;
				transform: scale(1.1);
			}
		}
		@media (max-width: $mq-max-width-mobile) {
			justify-content: flex-start;
		}
	}
	
	&.isSticky .saved {
		display: flex !important;
		margin: 0 $s1;
	}

	.icon-bookmark {
		position: relative;
		height: 30px;

		&:after {
			content: url("../assets/images/icon-bookmark-on.svg");
		}

		&.empty:after {
			position: absolute;
			content: url("../assets/images/icon-bookmark-off.svg");
			top: -9px;
			left: 0;
		}
	}

}

#facet-toggle {
	display: none;
	border: none;
	background: $off-white;
	text-transform: uppercase;
	background: none;
	padding: 0.5rem;
	height: 51px;
	margin-right: $s1;
	position: relative;
	width: 40px;
	cursor: pointer;

	span.icon-filter:after {
		position: absolute;
		content: url("../assets/images/icon-filter.svg");
		width: 28px;
		top: 12px;
		left: 5px;
	}
}

.facets-results {
	display: flex;
}


@media (max-width: $mq-max-width-desktop-sml) {
	.top-options {
		.type-toggles {
			font-size: 0.9rem;
			.type-toggle {
				margin-bottom: 0.5rem;
				&:not(:last-of-type) {
					&:after {
						content: "";
						margin: 0 0.5rem;
						height: 100%;
						width: 1px;
						border-right: solid 1px $dark-gray1;
					}
				}
			}
		}
	}
}

@media (max-width: $mq-max-width-tablet) {
	.results-container,
	.no-results {
		padding: 0 $side-padding-mobile $s4;
	}

	.facets-results {
		flex-direction: column;
	}

	.top-options {
		flex-direction: column;
		align-items: flex-start;
		margin-bottom: $s1;
		&.isSticky{
			.type-toggles{
				display: none;
			}
			.sort-view{
				padding-top: 0.875rem;
			}
		}
		.type-toggles {
			justify-content: flex-start;
			font-size: 0.875rem;
			height: 36px;
		}
		
		.sort-view {
			margin-top: 0;
			width: 100%;

			.view-toggle {
				margin-left: $s1;
			}
		}

		#facet-toggle {
			display: block;
		}
	}
}

@media (max-width: $mq-max-width-mobile) {
	.results-container,
	.no-results {
		padding: 0 $side-padding-mobile $s4;
	}

	.facets-results {
		flex-direction: column;
	}

	.top-options {
		flex-direction: column;
		align-items: flex-start;
		margin-bottom: $s1;
		padding-top: .5rem;

		.type-toggles {
			justify-content: flex-start;
			font-size: 0.875rem;
			height: 36px;
			overflow-x: auto;
			white-space: nowrap;
			width: 100%;

			.type-toggle{
				font-size: 0.875rem;
				margin-bottom: 1.25rem;
				margin-left: 0 !important;

				&:not(:last-child):after{
					margin: 0 0.438rem;
				}
			}
		}
		
		.sort-view {
			margin-top: 0;
			width: 100%;
			overflow-x: auto;

			.view-toggle {
				margin-left: $s1;
			}

			button {
				margin: 0 .25rem !important;

				&:after {
					transform: scale(.725) !important;
				}
			}


		}

		#facet-toggle {
			display: block;
		}

		&.isSticky .saved {
			margin: 0 .125rem;
		}
	}
}

.print-search-query {
	display: none;

	dl {
		width: 100%;
		margin-bottom: 2rem;

		dt,
		dd {
			display: inline-block;
			margin: 0.25rem 0;
		}
		dt {
			font-family: "FuturaBold";
			width: 30%;
			min-width: 150px;
			font-weight: bold;
		}
		dd {
			font-family: "Futura";
			margin-right: 5px;
		}
		.print-facet {
			margin-top: 0.5rem;
			padding-top: 0.5rem;

			& + .print-facet {
				margin: 0;
				padding: 0;
				border-top: none;
			}
		}
	}
}
.nav-link.saved{
	align-items: center;
	text-decoration: none;

	.saved-count span{
		font-weight: 700;
		font-size: 0.875rem;
		margin-right: 0.188rem;
		color: #4A4A4A;
	}
}

@media print {
	.more-links,
	.top-options {
		display: none;
		background: $white;
	}

	.print-search-query {
		display: block;
	}
}
</style>
